import React, { useState, useMemo, useEffect, useRef, useCallback, memo } from "react";
import { fetchFromStrapi } from "../api/strapi";
import { SkeletonBlock, EmptyState } from "./SkeletonUI";

/* ================= COMPONENTS ================= */

const GalleryItem = memo(({ image, index, onClick }) => {
  const [loaded, setLoaded] = useState(false);

  const handleClick = useCallback(() => {
    onClick(index);
  }, [onClick, index]);

  return (
    <div className="pg-item" onClick={handleClick}>
      <img
        src={image.src}
        alt={image.title}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        className={loaded ? "loaded" : "loading"}
      />
    </div>
  );
});

const Lightbox = ({ images, index, onClose, onNext, onPrev }) => {
  if (index === null) return null;

  const img = images[index];

  return (
    <div className="pg-lightbox" onClick={onClose}>
      <button className="close" onClick={onClose}>×</button>
      <button className="prev" onClick={(e) => { e.stopPropagation(); onPrev(); }}>‹</button>
      <img src={img.src} alt="" onClick={(e) => e.stopPropagation()} />
      <button className="next" onClick={(e) => { e.stopPropagation(); onNext(); }}>›</button>
    </div>
  );
};

// Cloudinary HEIC and other unsupported formats need f_auto to serve browser-compatible versions
function toWebCompatible(url) {
  if (!url || !url.includes("res.cloudinary.com")) return url;
  return url.replace("/upload/", "/upload/f_auto,q_auto/");
}

/* ================= MAIN ================= */

export default function CompanyGallery() {
  const [galleryData, setGalleryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [visible, setVisible] = useState(8);
  const [lightbox, setLightbox] = useState(null);
  const loadRef = useRef();

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await fetchFromStrapi("/ecl-about-us-galleries");
        const records = res?.data || [];
        console.log("Fetched gallery records:", records);
        const items = records.flatMap((record) => {
          const attr = record.attributes;
          const category = attr.title;
          const imgs = attr.images?.data || [];
          return imgs.map((img) => ({
            id: `${record.id}-${img.id}`,
            src: toWebCompatible(img.attributes.url),
            category,
            title: category,
          }));
        });
        setGalleryData(items);
      } catch (err) {
        console.error("Error fetching gallery:", err);
        setError("Failed to load gallery.");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const categories = useMemo(() => {
    const unique = [...new Set(galleryData.map((i) => i.category))];
    return [{ id: "all", label: "All" }, ...unique.map((c) => ({ id: c, label: c }))];
  }, [galleryData]);

  const images = useMemo(() => {
    return filter === "all"
      ? galleryData
      : galleryData.filter((i) => i.category === filter);
  }, [filter, galleryData]);

  // infinite scroll — re-attach after data loads so the sentinel exists in the DOM
  useEffect(() => {
    if (loading) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVisible((v) => v + 6);
      },
      { threshold: 0, rootMargin: "200px" }
    );
    if (loadRef.current) obs.observe(loadRef.current);
    return () => obs.disconnect();
  }, [loading, filter]);

  // preload next/prev for lightbox
  useEffect(() => {
    if (lightbox === null || images.length === 0) return;
    const next = new window.Image();
    next.src = images[(lightbox + 1) % images.length].src;
    const prev = new window.Image();
    prev.src = images[(lightbox - 1 + images.length) % images.length].src;
  }, [lightbox, images]);

  if (loading) {
    return (
      <div className="pg-wrapper">
        <div className="pg-masonry">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="pg-item">
              <SkeletonBlock height={220} radius={0} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || galleryData.length === 0) {
    return (
      <div className="pg-wrapper">
        <EmptyState
          title="Gallery unavailable"
          description={error || "No gallery images found."}
        />
      </div>
    );
  }

  return (
    <div className="pg-wrapper">
      <div className="pg-filters">
        {categories.map((c) => (
          <button
            key={c.id}
            className={filter === c.id ? "active" : ""}
            onClick={() => {
              setFilter(c.id);
              setVisible(8);
            }}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="pg-masonry">
        {images.slice(0, filter === "all" ? 15 : visible).map((img, i) => (
          <GalleryItem key={img.id} image={img} index={i} onClick={setLightbox} />
        ))}
      </div>

      {filter !== "all" && <div ref={loadRef} style={{ height: 20 }} />}

      <Lightbox
        images={images}
        index={lightbox}
        onClose={() => setLightbox(null)}
        onNext={() => setLightbox((i) => (i + 1) % images.length)}
        onPrev={() => setLightbox((i) => (i - 1 + images.length) % images.length)}
      />
    </div>
  );
}
